import * as Button from "@/shared/utils/button";
import {ElementSelector, mergeProperties, saveJQuerySelector, writeElementBodyMessage} from "@/shared/utils/misc";
import {allowPortalledPopupFocus} from "@/shared/ui/legacyModalFocusInterop";

declare let $clamp: (element: HTMLElement, options: { clamp: number }) => void;

allowPortalledPopupFocus();


export const DEFAULT_PARENT: ElementSelector = document.body;

export type ButtonAction = (modal: Modal, button?: JQuery) => void;
export type CloseAction = (modal: Modal, reason: string) => void;
export type OpenAction = (modal: Modal) => void;
export type ModalSize = "modal-xs" | "modal-sm" | "" | "modal-lg" | "modal-xl" | "modal-full";
export type TitleSize = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

export const CLOSE_REASON_DATA_KEY = "close-reason";
export const CLOSE_REASON_INTERNAL = "internal";
export const CLOSE_REASON_EXTERNAL = "external";

// alert dialogs sit above the Base UI Modal primitive (--zindex-modal 1091) and below the Base UI
// AlertDialog primitive (--zindex-alert-dialog-background 1094); stacked alerts tie and resolve by DOM order
const ALERT_DIALOG_Z_INDEX = 1093;

export interface ModalButton {
	text?: string;
	icon?: string;
	action?: ButtonAction;
	css?: string;
	visible?: boolean;
	left?: boolean;
	isCheckbox?: boolean;
}

export interface ModalOptions {
	title?: string;
	titleLabel?: {
		label: string,
		css?: string
	};
	message?: string | JQuery;
	escapeText?: boolean;
	css?: {
		dialog?: string;
		header?: string;
		content?: string;
	};
	size?: ModalSize;
	titleSize?: TitleSize;
	parent?: ElementSelector;
	buttons?: Array<ModalButton>;
	onClose?: CloseAction;
	/**
	 * Called after modal is opened and the animation is finished
	 */
	onOpen?: OpenAction;
	/**
	 * Called before the modal is opened
	 */
	onBeforeOpen?: OpenAction;
	backdrop?: boolean | "static";
	keyboard?: boolean;
	destroyOnClose?: boolean;
	preventDoubleClickButtons?: boolean;
	footer?: boolean;
	closeOnOutsideClick?: boolean;
	closeButton?: boolean;
	/**
	 * If a lightbox image view is open and the modal would be closed, prevents the close of the modal instead.
	 * This is needed so ESC key presses do not close the modal if a lightbox is open.
	 * The lightbox library (used for full screen image views) should actually capture ESC keys presses, but it does not work.
	 */
	preventCloseIfLightboxOpen?: boolean;
	/**
	 * Alert dialogs (info/warn/error) stack above regular modals — including the Base UI Modal primitive — see fixBackdrop.
	 */
	alert?: boolean;
}

export interface RecordModalOptions extends ModalOptions {
	checkbox?: boolean;
}

export class Modal {
	protected readonly id: string;
	private readonly options: ModalOptions;
	private readonly context: JQuery;
	protected domModal?: JQuery;
	private locked: boolean;
	private readonly outsideClickProxy: (e: JQuery.TriggeredEvent) => void;
	private onCloseCallback?: CloseAction;

	constructor(options: ModalOptions = {}) {
		this.id = crypto.randomUUID();

		const defaultOptions: ModalOptions = {
			title: "dialog.info.title",
			message: "",
			escapeText: true,
			css: {
				header: "",
				content: ""
			},
			size: "",
			titleSize: "h4",
			parent: DEFAULT_PARENT,
			buttons: [{
				text: "confirmation.ok",
				css: "btn-primary"
			}],
			backdrop: "static",
			keyboard: true,
			preventDoubleClickButtons: true,
			footer: true,
			closeOnOutsideClick: false,
			closeButton: true,
		};

		options = mergeProperties(defaultOptions, options);

		this.context = saveJQuerySelector(options.parent);
		this.options = options;

		// Create a click proxy that is private to this instance
		this.outsideClickProxy = (e: JQuery.TriggeredEvent) => { this.clickedOutside(e); };
	}

	public getOptions(): ModalOptions {
		return this.options;
	}

	/**
	 * Shows the modal and adds it to the DOM.
	 * @returns {BootstrapModal.Modal} The created modal or itself if the modal is already shown
	 */
	public show(): this {
		if (this.domModal === undefined) {
			this.domModal = this.createModal();
			this.domModal.appendTo(this.context);

			this.domModal
				.on("hide.bs.modal", this.handleBootstrapHide)
				.on("hidden.bs.modal", this.handleBootstrapHidden)
				.on("show.bs.modal", this.handleBootstrapShow)
				.on("shown.bs.modal", this.handleBootstrapShown)
				.modal({
					backdrop: this.options.backdrop,
					keyboard: this.options.keyboard,
					show: true
				});

			if (!($(document.body).is(this.context))) {
				// The context of the modal is not the body element which means
				// we have to fix the backdrop by moving it to the desired context
				$("body > .modal-backdrop").appendTo(this.context);
			}

			if (this.options.closeOnOutsideClick) {
				$(document).on("mousedown touchend", this.outsideClickProxy);
			}
		} else {
			this.domModal.modal("show");
		}
		return this;
	}

	/**
	 * Toggles the button visibility at the specified index
	 * @param {number|string} button if number the index of the button, or if string the className of the button
	 * @param {boolean} visible whether the button should be visible or not
	 */
	public toggleButton(button: number | string, visible?: boolean) {
		if (typeof button === "number") {
			const index = button;
			if (index >= 0 && index < this.options.buttons.length) {
				if (visible == null) {
					visible = !this.options.buttons[index].visible;
				}
				this.options.buttons[index].visible = visible;
				$(this.getButtons().get(index)).toggle(visible);
			}
		} else {
			const className = button;
			for (const btn of this.options.buttons) {
				if (btn.css && btn.css.indexOf(className) >= 0) {
					btn.visible = visible;
				}
			}
			this.getButtons(className).toggle(visible);
		}
	}

	/**
	 * Changes the button title at the specified index
	 * @param {number|string} button if number the index of the button, or if string the className of the button
	 * @param {string} text the new title of the button
	 */
	public setButtonText(button: number | string, text: string) {
		if (typeof button === "number") {
			const index = button;
			if (index >= 0 && index < this.options.buttons.length) {
				this.options.buttons[index].text = text;
				$(this.getButtons().get(index)).text(text);
			}
		} else {
			const className = button;
			for (const btn of this.options.buttons) {
				if (btn.css.indexOf(className) >= 0) {
					btn.text = text;
				}
			}
			this.getButtons(className).text(text);
		}
	}

	public destroy() {
		if (this.domModal != null) {
			this.domModal.remove();
			this.domModal = undefined;
		}
	}

	public resetLoadingButtons() {
		this.getButtons().each((index, elem) => {
			Button.resetLoading(elem);
		});
	}

	protected createModal(): JQuery {
		const modal = this.createModalHtml();
		this.writeTitle(modal, false);
		this.writeTitleLabel(modal);
		this.writeMessage(modal);
		this.addButtons(modal);
		return modal;
	}

	private createModalHtml(): JQuery {
		return $(`<div id="modal_${this.id}" class="modal fade" tabindex="-1">
						<div class="modal-dialog ${this.options.size} ${this.options.css.dialog}">
							<div class="modal-content ${this.options.css.content}">
								<div class="modal-header ${this.options.css.header}">
									${this.options.closeButton ? `<button type="button" class="close" data-dismiss="modal" title="${messages["dialog.close"]}"><i class="fal fa-times"></i></button>` : ""}
									<${this.options.titleSize} class="modal-title"></${this.options.titleSize}>
								</div>
								<div class="modal-body"></div>
								${this.options.footer ? `<div class="modal-footer"></div>` : ""}
							</div>
						</div>
					</div>`);
	}

	private addButtons(modal: JQuery) {
		const modalFooter = modal.find(".modal-footer");
		for (const button of this.options.buttons) {
			if (button.isCheckbox) {
				const checkboxElement = this.createCheckbox(Modal.localize(button.text), button.css, button.action, button.visible, button.left);
				modalFooter.append(checkboxElement);
			} else {
				const buttonElement = this.createButton(Modal.localize(button.text), button.css, button.action, button.visible, button.left, button.icon);
				modalFooter.append(buttonElement);
			}
		}
	}

	private createButton(text: string = "", css: string = "btn-default", action?: ButtonAction, visible: boolean = true, left: boolean = false, icon: string = null): JQuery {
		// margin-top: -2px required to visually center the icon,
		// for some reason the dynamically added icon is not completely identical to the statically added one,
		// even though all computed properties are the same.
		const innerHtml = icon ? `<i class="${icon}" style="margin-top: -2px"></i> ${text}` : text;
		const buttonElement = $(`<button type="button" class="btn ${css}${left ? " pull-left" : ""}">${innerHtml}</button>`);
		buttonElement.toggle(visible);

		if (action == null) {
			buttonElement.attr("data-dismiss", "modal");
		} else {
			buttonElement.click(() => {
				if (this.options.preventDoubleClickButtons) {
					Button.setLoading(buttonElement);
				}
				action(this, buttonElement);
			});
		}

		return buttonElement;
	}

	private createCheckbox(text: string = "", css: string = "", action?: ButtonAction, visible: boolean = true, left: boolean = false): JQuery {
		const checkboxElement = $(
			`<label class="mt-10 checkbox-inline checkbox-inline-xs ${left ? "pull-left" : ""}">
							${text}
							<input type="checkbox" class="${css}" />
						</label>
			`);
		checkboxElement.toggle(visible);

		if (action == null) {
			checkboxElement.attr("data-dismiss", "modal");
		} else {
			checkboxElement.change(() => {
				action(this, checkboxElement.find("input"));
			});
		}

		return checkboxElement;
	}

	private readonly handleBootstrapHide = () => {
		if (this.locked) {
			return false;
		}

		if (this.options.preventCloseIfLightboxOpen) {
			const lightbox = document.getElementById("lightboxOverlay");
			if (lightbox && lightbox.style.display !== "none") {
				return false;
			}
		}
	}

	private readonly handleBootstrapHidden = () => {
		if (this.options.preventDoubleClickButtons) {
			this.domModal.find(".modal-footer").find("button").each((index, elem) => {
				Button.resetLoading(elem);
			});
		}
		const closeReason: string = this.domModal.data(CLOSE_REASON_DATA_KEY) as string || CLOSE_REASON_INTERNAL;
		if (this.options.onClose != null) {
			this.options.onClose(this, closeReason);
		}
		if (this.onCloseCallback != null) {
			this.onCloseCallback(this, closeReason);
		}
		if (this.options.destroyOnClose === true) {
			this.destroy();
		}
		this.fixScrollbar();
	}

	private readonly handleBootstrapShow = () => {
		this.fixBackdrop();
		$(this).triggerHandler("show.bs.modal");
		if (this.options.onBeforeOpen != null) {
			this.options.onBeforeOpen(this);
		}
	}

	private readonly handleBootstrapShown = () => {
		if (this.options.onOpen != null) {
			this.options.onOpen(this);
		}
	}

	/* Backdrop z-index fix
	  * Correct backdrops when you have modals over modals
	  * https://stackoverflow.com/questions/19305821/multiple-modals-overlay
	  *
	  * Dialog stack (bottom→top): legacy modals (1050 + 10 per stacked modal) → Base UI modals (--zindex-modal 1091)
	  * → legacy alert dialogs (1093) → Base UI alert dialogs (--zindex-alert-dialog 1095) → popups (--zindex-popup 1099+)
	*/
	private fixBackdrop() {
		const zIndex = this.options.alert === true
			? ALERT_DIALOG_Z_INDEX
			: 1040 + (10 * $(".modal:visible").length);
		this.domModal.css("z-index", zIndex);
		setTimeout(() => {
			$(".modal-backdrop").not(".modal-stack").css("z-index", zIndex - 1).addClass("modal-stack");
		}, 0);
	}

	/* Scrollbar fix
	 * If you have a modal on your page that exceeds the browser height, then you can't scroll in it when closing an second modal.
	 * https://stackoverflow.com/questions/19305821/multiple-modals-overlay
	 */
	private fixScrollbar() {
		if ($(".modal:visible").length > 0) {
			$(document.body).addClass("modal-open");
		}
	}

	/**
	 * Closes the modal if its not locked and calls the internal listener. This will result in the calling of
	 * the onClose function that was provided in the options.
	 *
	 * You can specify a reason string why it was closed. The default reason is "external". If the modal is
	 * closed by clicking the ( x ) on the top right, by clicking outside of the modal or by pressing ESC
	 * @param {string} reason
	 */
	public close(reason: string = CLOSE_REASON_EXTERNAL) {
		if (this.domModal != null) {
			this.domModal.data(CLOSE_REASON_DATA_KEY, reason);
			this.domModal.modal("hide");
		}
	}

	public onClose(closeCallback: CloseAction) {
		this.onCloseCallback = closeCallback;
	}

	public setTitle(title: string, clampTitle: boolean = false) {
		this.options.title = Modal.localize(title);
		if (this.domModal != null) {
			this.writeTitle(this.domModal, clampTitle);
		}
	}

	private writeTitle(modal: JQuery, clampTitle: boolean) {
		const modalTitle = modal.find(".modal-title");
		const message = Modal.localize(this.options.title);

		if (this.options.escapeText === false) {
			modalTitle.html(message);
		} else {
			modalTitle.text(message);
		}

		if (clampTitle && modalTitle.length > 0) {
			$clamp(modalTitle[0], {
				clamp: 1
			});
		}
	}

	public setTitleLabel(label: string, css?: string) {
		this.options.titleLabel = {
			label: label,
			css: css || "label-default"
		};
		if (this.domModal != null) {
			this.writeTitleLabel(this.domModal);
		}
	}

	private writeTitleLabel(modal: JQuery) {
		const modalTitle = modal.find(".modal-title");
		let modalTitleLabel = modalTitle.find(".modal-title-label");
		if (this.options.titleLabel) {
			if (modalTitleLabel.length === 0) {
				modalTitleLabel = $("<span>").appendTo(modalTitle).append($("<span>"));
			}
			modalTitleLabel.removeClass().addClass(".modal-title-label label").addClass(this.options.titleLabel.css);
			const message = Modal.localize(this.options.titleLabel.label);
			if (this.options.escapeText === false) {
				modalTitleLabel.html(message);
			} else {
				modalTitleLabel.children("span").text(message);
			}
		} else {
			modalTitleLabel.remove();
		}
	}

	public setMessage(message: string | JQuery) {
		this.options.message = message;
		if (this.domModal != null) {
			this.writeMessage(this.domModal);
		}
	}

	private writeMessage(modal: JQuery) {
		const modalBody = modal.find(".modal-body");
		writeElementBodyMessage(modalBody, this.options.message, this.options.escapeText);
	}

	/**
	 * Locks the modal which disallows it to disappear by clicking outside of it, by clicking close ( x ) or
	 * by pressing ESC.
	 */
	public lock() {
		this.locked = true;
	}

	public unlock() {
		this.locked = false;
	}

	/**
	 * Returns a JQuery object selecting <button> elements inside the modal
	 * @param {string} className
	 * @returns {JQuery}
	 */
	public getButtons(className: string = null): JQuery {
		if (this.domModal != null) {
			const classSelector = className ? "." + className : "";
			return this.domModal.find(".modal-footer").find("button" + classSelector);
		} else {
			return $();
		}
	}

	/**
	 * Returns a JQuery object selecting <input[type='checkbox']> elements inside the modal
	 * @param {string} className
	 * @returns {JQuery}
	 */
	public getCheckboxes(className: string = null): JQuery {
		if (this.domModal != null) {
			const classSelector = className ? "." + className : "";
			return this.domModal.find(".modal-footer").find("input[type='checkbox']" + classSelector);
		} else {
			return $();
		}
	}

	/**
	 * Returns the value of the reason input for ConfirmPromptModals
	 * @return {string} or null if the modal is not shown or no ConfirmPromptModal
	 */
	public getReason(): string {
		return this.domModal != null ? this.domModal.find(`textarea[id="prompt"]`).val() as string : null;
	}

	private static localize(text?: string): string {
		if (text == null) {
			return "";
		} else {
			return messages[text] || text;
		}
	}

	public find(selector: string): JQuery {
		return this.domModal.find(selector);
	}

	public getDomModal(): JQuery {
		return this.domModal;
	}

	private clickedOutside(e: JQuery.TriggeredEvent) {
		const target = $(e.target);
		// if the page is clicked anywhere except within the input or modal itself then close the modal
		if (e.type !== "focusin" /* ie modal dialog fix */ &&
			target.closest(this.domModal.find(".modal-content")).length === 0) {
			this.close(CLOSE_REASON_INTERNAL);
		}
	}
}

export class RecordModal extends Modal {

	private checkbox: JQuery;
	private recordModelOptions: RecordModalOptions;

	constructor(options: RecordModalOptions = {}) {
		super(options);
		const defaultOptions: RecordModalOptions = {
			checkbox: false
		};
		this.recordModelOptions = mergeProperties(defaultOptions, options);
	}

	protected createModal(): JQuery {
		const modal = super.createModal();
		this.addCheckbox(modal);
		return modal;
	}

	protected addCheckbox(modal: JQuery) {
		this.checkbox = $(`<label for="anotherRecordCheckbox" class="checkbox-inline pr-15 display-inline-block" title="${messages["dialog.another.checkbox.tooltip"]}">
											<input type="checkbox" id="anotherRecordCheckbox" />${messages["dialog.another.checkbox"]}
										</label>`);
		this.checkbox.toggle(this.recordModelOptions.checkbox);
		modal.find(".modal-footer").prepend(this.checkbox);
	}

	public getCheckboxValue(): boolean {
		return this.domModal.find(".modal-footer").find("#anotherRecordCheckbox").is(":checked");
	}

	public setCheckboxValue(val: boolean) {
		this.domModal.find(".modal-footer").find("#anotherRecordCheckbox").prop("checked", val);
	}

	public toggleCheckbox(showCheckbox: boolean) {
		if (this.checkbox !== undefined) {
			this.checkbox.toggle(showCheckbox);
		} else {
			this.recordModelOptions.checkbox = showCheckbox;
		}
	}
}

/**
 * Creates and shows a basic modal with BootstrapModals own default options
 * @param {BootstrapModal.ModalOptions} options
 * @returns {BootstrapModal.Modal}
 */
export function info(options?: ModalOptions): Modal {
	const infoConfig = {
		css: {
			header: "bg-primary"
		},
		titleSize: "h5" as TitleSize,
		destroyOnClose: true,
		backdrop: true,
		alert: true
	};
	const modalOptions = mergeProperties(infoConfig, options);
	return new Modal(modalOptions).show();
}

/**
 * Creates and shows a warning modal with default options for a basic warning modal
 * @param {BootstrapModal.ModalOptions} options
 * @returns {BootstrapModal.Modal}
 */
export function warn(options?: ModalOptions): Modal {
	const warnConfig = {
		css: {
			header: "bg-warning"
		},
		titleSize: "h5" as TitleSize,
		destroyOnClose: true,
		backdrop: true,
		alert: true
	};

	const modalOptions = mergeProperties(warnConfig, options);
	return new Modal(modalOptions).show();
}

/**
 * Creates and shows an error modal with default options for a basic error modal
 * @param {BootstrapModal.ModalOptions} options
 * @returns {BootstrapModal.Modal}
 */
export function error(options?: ModalOptions): Modal {
	const errorConfig = {
		title: "dialog.error.title",
		message: "dialog.error.unknown",
		css: {
			header: "bg-danger"
		},
		titleSize: "h5" as TitleSize,
		destroyOnClose: true,
		backdrop: true,
		alert: true
	};

	const modalOptions = mergeProperties(errorConfig, options);
	return new Modal(modalOptions).show();
}

export function trustBasedNoAccess(options?: ModalOptions): Modal {
	const warnConfig = {
		title: messages["dialog.warning.title"],
		message: messages["dialog.trustBasedTime.notAllowed.message"],
		css: {
			header: "bg-warning"
		},
		titleSize: "h5" as TitleSize,
		destroyOnClose: true,
		backdrop: true,
		alert: true
	};

	const modalOptions = mergeProperties(warnConfig, options) as ModalOptions;
	return new Modal(modalOptions).show();
}

export interface ConfirmModalOptions extends ModalOptions {
	yesLabel?: string;
	yesAction?: ButtonAction;
	yesCss?: string;
	noLabel?: string;
	noAction?: ButtonAction;
	noCss?: string;
}

export interface ConfirmPromptModalOptions extends ConfirmModalOptions {
	promptTitle?: string;
	promptNameTag?: string;
}

/**
 * Creates and shows a confirm modal with a yes and now button as default options.
 * You can specify the label and callbacks of this buttons in the options
 * @param {BootstrapModal.ConfirmModalOptions} options
 * @returns {BootstrapModal.Modal}
 */
export function confirm(options: ConfirmModalOptions = {}) {
	const confirmOptions = {
		title: "dialog.warning.title",
		css: {
			header: "bg-warning"
		},
		titleSize: "h5" as TitleSize,
		buttons: [{
			text: options.yesLabel || "confirmation.yes",
			action: (modal: Modal, button?: JQuery) => {
				if (options.yesAction != null) {
					options.yesAction(modal, button);
				} else {
					modal.close("yes");
				}
			},
			css: options.yesCss || "btn-danger"
		}, {
			text: options.noLabel || "confirmation.cancel",
			action: (modal: Modal, button?: JQuery) => {
				if (options.noAction != null) {
					options.noAction(modal, button);
				} else {
					modal.close("no");
				}
			},
			css: options.noCss || "btn-primary"
		}],
		onClose: (modal: Modal, reason: string) => {
			if (reason === CLOSE_REASON_INTERNAL) {
				if (options.noAction != null) {
					options.noAction(modal);
				}
			}
		},
		backdrop: true,
		destroyOnClose: true
	};

	const modalOptions = mergeProperties(confirmOptions, options);
	return new Modal(modalOptions).show();
}

/**
 * Creates and shows a confirm modal with a textarea and  a yes and now button as default options.
 * You can specify the label and callbacks of this textarea and buttons in the options
 * @param {BootstrapModal.ConfirmPromptModalOptions} options
 * @returns {BootstrapModal.Modal}
 */
export function confirmPrompt(options: ConfirmPromptModalOptions = {}) {
	const textarea = $(`<textarea id="prompt" class="form-control">`);
	if (options.promptNameTag) {
		textarea.attr("name", options.promptNameTag);
	}
	options.message = $("<div>").addClass("bootbox-body")
		.append($("<p>").append(options.message))
		.append($(`<form id="confirmPrompt">`)
			.append($("<div>").addClass("form-group")
				.append($("<label>").addClass("control-label").text(options.promptTitle || messages["confirmation.reason"]))
				.append(textarea))
		);
	return confirm(options);
}
